/**
 * Toast - Notification system for user feedback
 * Shows animated toasts for cart additions, errors, and success messages
 */
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FONT_FAMILY } from '../ShopStyles';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    
    // Auto remove
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
    
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message) => addToast(message, 'success'), [addToast]);
  const showError = useCallback((message) => addToast(message, 'error', 5000), [addToast]);
  const showCart = useCallback((message, productImage) => {
    return addToast({ text: message, image: productImage }, 'cart', 4000);
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, showSuccess, showError, showCart }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container - rendered via portal
const ToastContainer = ({ toasts, removeToast }) => {
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'toast-animations';
    styleSheet.textContent = `
      @keyframes toastSlideIn {
        from { 
          transform: translateX(100%); 
          opacity: 0;
        }
        to { 
          transform: translateX(0); 
          opacity: 1;
        }
      }
      @keyframes toastSlideOut {
        from { 
          transform: translateX(0); 
          opacity: 1;
        }
        to { 
          transform: translateX(100%); 
          opacity: 0;
        }
      }
      .toast-item {
        animation: toastSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
      .toast-item.removing {
        animation: toastSlideOut 0.2s ease-in forwards;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('toast-animations');
      if (existing) existing.remove();
    };
  }, []);

  if (toasts.length === 0) return null;

  return ReactDOM.createPortal(
    <div style={styles.container}>
      {toasts.map(toast => (
        <Toast 
          key={toast.id} 
          toast={toast} 
          onClose={() => removeToast(toast.id)} 
        />
      ))}
    </div>,
    document.body
  );
};

// Individual Toast
const Toast = ({ toast, onClose }) => {
  const { type, message } = toast;
  
  const icons = {
    success: '✓',
    error: '✕',
    cart: '🛒',
  };
  
  const colors = {
    success: { bg: '#dcfce7', border: '#86efac', text: '#166534', icon: '#22c55e' },
    error: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', icon: '#ef4444' },
    cart: { bg: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', border: '#d8b4fe', text: '#6b21a8', icon: '#9333ea' },
  };
  
  const color = colors[type] || colors.success;
  const isCartToast = type === 'cart' && typeof message === 'object';

  return (
    <div 
      className="toast-item"
      style={{
        ...styles.toast,
        background: color.bg,
        borderColor: color.border,
      }}
    >
      {/* Icon */}
      <div style={{
        ...styles.iconWrapper,
        background: color.icon,
      }}>
        {icons[type]}
      </div>
      
      {/* Content */}
      <div style={styles.content}>
        {isCartToast ? (
          <>
            <div style={styles.cartContent}>
              {message.image && (
                <img src={message.image} alt="" style={styles.productImage} />
              )}
              <span style={{ ...styles.text, color: color.text }}>{message.text}</span>
            </div>
          </>
        ) : (
          <span style={{ ...styles.text, color: color.text }}>{message}</span>
        )}
      </div>
      
      {/* Close Button */}
      <button onClick={onClose} style={styles.closeBtn}>×</button>
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed',
    top: '100px',
    right: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    zIndex: 9999,
    maxWidth: '380px',
    width: '100%',
    pointerEvents: 'none',
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '14px',
    border: '2px solid',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
    pointerEvents: 'all',
  },
  iconWrapper: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '700',
    color: '#fff',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  text: {
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.4,
  },
  cartContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  productImage: {
    width: '44px',
    height: '44px',
    borderRadius: '8px',
    objectFit: 'cover',
    border: '2px solid #fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  },
  closeBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.5)',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 1,
  },
};

export default ToastProvider;
